import { trpc } from "@/lib/trpc";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useCallback, useState } from "react";
import { type QboBanner as QboBannerData, qboBannerFor } from "./qbo-banner-state";

const STORAGE_KEY = "qbo-banner-dismissed";

/**
 * Dismissals live in localStorage, not the database: they are a per-person,
 * per-device "yes, I've seen it", and the keys carry the sync run id so a
 * later run with new information shows up again on its own.
 */
function readDismissed(): Set<string> {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return new Set();
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? new Set(parsed.filter((k) => typeof k === "string")) : new Set();
	} catch {
		// Private-browsing or a corrupted value. Showing the banner again is
		// the safe failure here — it is the whole notification channel.
		return new Set();
	}
}

function persistDismissed(keys: Set<string>): void {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
	} catch {
		// Nothing to do — the banner reappears next load, which is harmless.
	}
}

const TONE_STYLES: Record<QboBannerData["tone"], string> = {
	urgent: "border-[#fecaca] bg-[#fef2f2] text-[#991b1b]",
	warning: "border-[#fed7aa] bg-[#fff7ed] text-[#9a3412]",
	info: "border-[#bfdbfe] bg-[#eff6ff] text-[#1e40af]",
};

export function QboBanner() {
	const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
	const { data } = trpc.qbo.bannerStatus.useQuery(undefined, {
		// The nightly job is the source of change, so polling harder than this
		// buys nothing. Refetch on focus covers the "I just reconnected" case.
		staleTime: 5 * 60 * 1000,
	});

	const dismiss = useCallback((key: string) => {
		setDismissed((prev) => {
			const next = new Set(prev).add(key);
			persistDismissed(next);
			return next;
		});
	}, []);

	if (!data) return null;

	const banner = qboBannerFor(data, dismissed);
	if (!banner) return null;

	return (
		<output
			className={`flex items-center gap-3 border-b px-6 py-3 text-sm ${TONE_STYLES[banner.tone]}`}
		>
			<span className="flex-1">{banner.message}</span>
			<Link
				to={banner.ctaHref}
				className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
			>
				{banner.ctaLabel}
			</Link>
			<button
				type="button"
				aria-label="Dismiss notification"
				onClick={() => dismiss(banner.dismissKey)}
				className="shrink-0 rounded p-1 hover:bg-black/5"
			>
				<X className="h-4 w-4" />
			</button>
		</output>
	);
}
